import React, { Fragment } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';

import FormSummaryRow from './formSummaryRow';
import NoInformationFound from '../design/noInformationFound';
import BusinessPlansTable from './businessPlansTable';

import { GroupWrapper, GroupTitle, SummarySectionWrapper } from './css';
import {
  smbIndirectSalesChannels,
  keyDirectoryListingDetails,
  keyChannelId,
  keyOutlet,
  keyChannelName,
  fieldBAN,
  fieldExistingBusinessConnectBANNumber,
  fieldAvailableDiscountsAndPromotions,
  fieldCoperToFibreMigration,
  fieldC2fAutomatedActivation,
  fieldExistingCustomerBA,
  fieldExistingCustomerDomain,
  fieldIotProductSelected,
  fieldCustomerMigrating,
  fieldIotDeliveryOptions,
  fieldAdditionalLocalNumber,
  fieldAdditionalTollFreeNumber,
  fieldOfferConfiguration,
  couponTypeBillCredit
} from '../utils/constant';
import FormField from './formField';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { isSalesSupport } from '../utils/request';
import SummaryRatePlan from '../iot/viewOrderSummary/summaryRatePlan';
import SummaryRatePlanRenewal from '../iot/viewOrderSummary/summaryRatePlanRenewal';
import SummaryAddons from '../iot/viewOrderSummary/summaryAddons';
import SummaryAdditionalServices from '../iot/viewOrderSummary/summaryAdditionalServices';
import SummaryHardware from '../iot/viewOrderSummary/summaryHardware';
import SummaryVehicleInfo from '../iot/viewOrderSummary/summaryVehicleInfo';
import SummaryTankDetails from '../iot/viewOrderSummary/summaryTankDetails';
import SummaryTankDetailsFarmAndRanch from '../iot/viewOrderSummary/summaryTankDetailsFarmAndRanch';
import SummaryTrackerSummary from '../iot/viewOrderSummary/summaryTrackerSummary';
import SummaryTechnicalServices from '../iot/viewOrderSummary/summaryTechnicalServices';
import SummaryInstallServices from '../iot/viewOrderSummary/summaryInstallServices';
import DeliveryCharges from '../iot/orderDetailsSection/deliveryCharges';

import getFormattedPricing from '../iot/orderDetailsSection/utils';
import DevicesTableSummary from './devicesTableSummary';
import SummaryHardwarePowerFleet from '../iot/viewOrderSummary/summaryPowerFleetTable';
import SummaryTrackerPowerFleetSummary from '../iot/viewOrderSummary/summaryTrackerPowerFleetSummary';
import SummaryOrderNow from '../iot/viewOrderSummary/summaryOrderNow';
import AppliedCouponsSummaryTable from './appliedCouponsSummaryTable';
import SummaryGeotabBundles from '../iot/viewOrderSummary/summaryGeotabBundles';
import SummaryIotPopupProducts from '../iot/viewOrderSummary/summaryIotPopupProducts';

// Bill credit applies to site 1 only. On subsequent sites of a multisite order
// (currentSite > 1) exclude bill credit from any displayed Order Summary while
// keeping it in the persisted `appliedCoupons`. This is used by every summary
// surface (form review, combined multisite summary, and the dashboard
// "View Order" popup), each of which passes the relevant order/site as
// `orderData`.
const getDisplayCoupons = backendObject => {
  const coupons = _.get(backendObject, 'appliedCoupons', []);
  if (!Array.isArray(coupons)) return coupons;
  const siteNumber = Number(_.get(backendObject, 'currentSite', 1)) || 1;
  if (siteNumber > 1) {
    return coupons.filter(
      c =>
        !(c && c.couponType ? c.couponType : '')
          .toLowerCase()
          .includes(couponTypeBillCredit)
    );
  }
  return coupons;
};

// Order-review summary keys for `IoTPopupButton` fields, mapped to the field
// name whose products they render. Each popup gets its own titled group, so
// adding another popup is one entry here rather than a new branch below.
const IOT_POPUP_SUMMARY_FIELDS = {
  iotSummaryPhillipsConnectAssetTracker: 'iotPopupButton',
  iotSummaryGoFocusPlus: 'goFocusPlusPopupButton'
};

// Pre-dates per-field keys, so it is only a safe fallback for the first popup.
const LEGACY_IOT_POPUP_PRODUCTS = {
  iotPopupButton: 'iotPopupProducts'
};

const FormSummaryGroup = ({ data, orderData: backendObject }) => {
  const localizedString = useLocalizedStringsContext();
  const {
    contentfulValues: {
      yes,
      no,
      valueFleetcomplete,
      valueGeotab,
      valueGeoforce
    },
    productInOrderForOptions: { googleWorkspace, microsoft365 },
    existingCustomerBA_QuestionLabels: {
      googleWorkspaceCustomer,
      googleWorkspaceDomain,
      microsoft365Customer,
      microsoft365Domain
    },
    iotOrderDetailsSection: { labelFieldCustomerMigrationFC }
  } = localizedString;
  const isFieldVisible = backendFieldValidation => {
    let isVisible = true;
    if (backendFieldValidation) {
      for (let i = 0; i < backendFieldValidation.length; i += 1) {
        const {
          fields: { key: fieldKey, value }
        } = backendFieldValidation[i];
        isVisible =
          isVisible &&
          (value.split('|').includes(backendObject[fieldKey]) ||
            (value.split('|').includes('undefined') &&
              typeof backendObject[fieldKey] === 'undefined'));
      }
    }
    return isVisible;
  };

  const getInternetPlan = speed => {
    const localizedStrings = useLocalizedStringsContext();
    const {
      summaryForm: { plans }
    } = localizedStrings;

    return _.get(plans, speed, '');
  };

  const getFieldValue = (fieldKey, fieldValue) => {
    if (!fieldValue) return false;
    if (fieldKey === fieldAvailableDiscountsAndPromotions)
      return fieldValue.join(' + ');
    return [
      'currentInternetPlan',
      'plan',
      'whatSpeedAreTheyUpgradingTo'
    ].includes(fieldKey)
      ? getInternetPlan(fieldValue)
      : fieldValue;
  };

  const getUpdateLabel = (fieldName, fieldLabel) => {
    let updatedLabel = fieldLabel;
    if (
      backendObject.productInOrderFor === googleWorkspace &&
      fieldName === fieldExistingCustomerBA
    ) {
      updatedLabel = googleWorkspaceCustomer;
    }
    if (
      backendObject.productInOrderFor === microsoft365 &&
      fieldName === fieldExistingCustomerBA
    ) {
      updatedLabel = microsoft365Customer;
    }
    if (
      backendObject.productInOrderFor === googleWorkspace &&
      fieldName === fieldExistingCustomerDomain
    ) {
      updatedLabel = googleWorkspaceDomain;
    }
    if (
      backendObject.productInOrderFor === microsoft365 &&
      fieldName === fieldExistingCustomerDomain
    ) {
      updatedLabel = microsoft365Domain;
    }
    return updatedLabel;
  };

  const {
    fields: { backendFields, backendKey, name, backendFieldValidation }
  } = data;

  if (!isFieldVisible(backendFieldValidation)) return null;

  // Additional devices section: when no devices were added and every answer
  // is "No", show "No information available" instead of an empty table.
  const hasDevicesTable = backendFields.some(
    ({ fields: { key } }) => key === 'devicesAdd'
  );
  if (hasDevicesTable && _.isEmpty(backendObject.devices)) {
    const hasPositiveAnswer = backendFields.some(({ fields: { key } }) => {
      const answer = backendObject[key];
      return key !== 'devicesAdd' && !!answer && answer !== no;
    });
    if (!hasPositiveAnswer) return <NoInformationFound />;
  }

  const fieldData = backendFields.map((backendField, index) => {
    const {
      sys: {
        contentType: {
          sys: { id }
        },
        id: sysId
      },
      fields: { key, backendFieldValidation: subBackendFieldValidation }
    } = backendField;
    const keyId = `summary-${backendKey}${index}${sysId}${name}`;

    if (!isFieldVisible(subBackendFieldValidation)) return null;
    if (key === 'iotSummaryRatePlan') {
      return (
        <SummarySectionWrapper>
          <SummaryRatePlan
            orderData={backendObject}
            total={_.get(backendObject, 'total.ratePlan', 0)}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryRatePlanRenewal') {
      return (
        <SummarySectionWrapper>
          <SummaryRatePlanRenewal
            renewalSubscriberList={_.get(
              backendObject,
              'renewalSubscriberList',
              []
            )}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryTechnicalServices') {
      return (
        <SummarySectionWrapper>
          <SummaryTechnicalServices
            technicalServices={_.get(backendObject, 'technicalServices', {})}
            total={_.get(backendObject, 'total.technicalServices', 0)}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'devicesAdd' && backendObject.devices) {
      return (
        <SummarySectionWrapper>
          <DevicesTableSummary orderData={backendObject.devices} />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryGeotabBundles') {
      return (
        <SummarySectionWrapper>
          <SummaryGeotabBundles
            data={_.get(backendObject, 'geotabBundles', {})}
            total={_.get(backendObject, 'total.geotabBundles', 0)}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryAddons') {
      return (
        <SummarySectionWrapper>
          <SummaryAddons
            addons={_.get(backendObject, 'addons', {})}
            total={_.get(backendObject, 'total.addons', 0)}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryOrderNow') {
      return (
        <SummarySectionWrapper>
          <SummaryOrderNow
            orderNow={_.get(backendObject, 'orderNow', {})}
            total={_.get(backendObject, 'total.orderNow', 0)}
          />
        </SummarySectionWrapper>
      );
    }
    if (IOT_POPUP_SUMMARY_FIELDS[key]) {
      const fieldName = IOT_POPUP_SUMMARY_FIELDS[key];
      const popupProducts =
        _.get(backendObject, `${fieldName}.products`) ||
        // Orders saved before popups were keyed per field only ever had one.
        _.get(backendObject, LEGACY_IOT_POPUP_PRODUCTS[fieldName], []);
      return (
        <SummarySectionWrapper>
          <SummaryIotPopupProducts products={popupProducts} />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotAdditionalServices') {
      return (
        <SummarySectionWrapper>
          <SummaryAdditionalServices
            additionalServices={_.get(backendObject, 'additionalServices', {})}
            total={_.get(backendObject, 'total.additionalServices', 0)}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryInstallServices') {
      return (
        <SummarySectionWrapper>
          <SummaryInstallServices
            installServices={_.get(backendObject, 'installServices', {})}
            total={_.get(backendObject, 'total.installServices', 0)}
            isGeotabProduct={
              backendObject[fieldIotProductSelected] === valueGeotab ||
              backendObject[fieldIotProductSelected] === valueGeoforce
            }
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryHardware') {
      return (
        <SummarySectionWrapper>
          <SummaryHardware
            orderData={backendObject}
            total={_.get(backendObject, 'total', {})}
          />
        </SummarySectionWrapper>
      );
    }
    if (
      [
        'iotSummaryMonthlySoftwareSubscription',
        'iotSummaryHardwarePowerFleet',
        'iotSummaryProfessionalServices',
        'iotSummaryHardwareColdChainPowerFleet',
        'iotSummaryProfessionalServicesColdChain'
      ].includes(key)
    ) {
      return (
        <SummarySectionWrapper>
          <SummaryHardwarePowerFleet
            orderData={backendObject}
            total={_.get(backendObject, 'total', {})}
            itemKey={key}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryVehicleInfo') {
      return (
        <SummarySectionWrapper>
          <SummaryVehicleInfo
            vehicleInfo={_.get(backendObject, 'vehicleInfo', [])}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryTankDetails') {
      return (
        <SummarySectionWrapper>
          <SummaryTankDetails
            tankDetails={_.get(backendObject, 'tankDetails', [])}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryTankDetailsFarmAndRanch') {
      return (
        <SummarySectionWrapper>
          <SummaryTankDetailsFarmAndRanch
            tankDetails={_.get(backendObject, 'farmAndRanchTankDetails', [])}
          />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryTracker') {
      return (
        <SummarySectionWrapper>
          <SummaryTrackerSummary orderData={backendObject} />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryPowerFleetTracker') {
      return (
        <SummarySectionWrapper>
          <SummaryTrackerPowerFleetSummary orderData={backendObject} />
        </SummarySectionWrapper>
      );
    }
    if (key === 'iotSummaryDeliveryCharges') {
      return (
        <SummarySectionWrapper>
          <DeliveryCharges
            ratePlans={_.get(backendObject, 'ratePlan', {})}
            productSelected={_.get(backendObject, fieldIotProductSelected, '')}
          />
        </SummarySectionWrapper>
      );
    }
    if (
      key === 'businessAppPlans' &&
      backendObject.businessAppPlans &&
      backendObject.businessAppPlans.length > 0
    ) {
      return (
        <SummarySectionWrapper>
          <BusinessPlansTable
            businessAppPlans={backendObject.businessAppPlans}
            onDelete={() => {}}
          />
        </SummarySectionWrapper>
      );
    }
    if (id === 'formSummaryStepGroup') {
      // Check if this nested group is Offer Configuration and render table before it
      const groupName = backendField.fields.name || '';
      const groupBackendKey = backendField.fields.backendKey || '';
      const isOfferConfigGroup =
        (groupName.toLowerCase().includes('offer') &&
          groupName.toLowerCase().includes('configuration')) ||
        groupBackendKey === fieldOfferConfiguration ||
        groupName === fieldOfferConfiguration;
      const appliedCoupons = getDisplayCoupons(backendObject);
      const shouldShowTable =
        isOfferConfigGroup &&
        appliedCoupons &&
        Array.isArray(appliedCoupons) &&
        appliedCoupons.length > 0;

      return (
        <GroupWrapper style={{ marginBottom: '1em' }} key={keyId}>
          <GroupTitle style={{ marginBottom: '1em' }}>
            {backendField.fields.name}
          </GroupTitle>
          {shouldShowTable && (
            <AppliedCouponsSummaryTable appliedCoupons={appliedCoupons} />
          )}
          <FormSummaryGroup data={backendField} orderData={backendObject} />
        </GroupWrapper>
      );
    }
    if (id === 'repeatedSummaryGroup') {
      const keyValue = backendObject[backendField.fields.key];
      const currentFieldName = backendField.fields.name;
      const subObjectList = (Array.isArray(keyValue) && keyValue) || [];
      if (!subObjectList.length) return null;
      return (
        <GroupWrapper style={{ marginBottom: '1em' }}>
          {subObjectList.map((item, i) => {
            let subSecKey =
              subObjectList.length > 1
                ? `${currentFieldName} ${i + 1}`
                : currentFieldName;
            if (backendField.fields.key === keyDirectoryListingDetails) {
              subSecKey = item.title;
            }
            return (
              <Fragment key={subSecKey}>
                <GroupTitle style={{ marginBottom: '1em' }}>
                  {subSecKey}
                </GroupTitle>
                <FormSummaryGroup data={backendField} orderData={item} />
              </Fragment>
            );
          })}
        </GroupWrapper>
      );
    }
    const { key: fieldKey, value: fieldValue } = backendField.fields;
    let { label } = backendField.fields;
    const value = backendObject[fieldKey];
    const {
      role,
      channels,
      handleFormField,
      error,
      ref,
      isSelectAllowed
    } = backendObject;

    if (
      fieldKey === 'email_administratorContact' &&
      _.get(backendObject, fieldC2fAutomatedActivation, '') === yes
    ) {
      return (
        <>
          <FormSummaryRow
            key={fieldKey}
            label={label}
            value="rpabot.businessconnectvoicesetup@telus.com"
          />
          <FormSummaryRow
            key="customerEmail_administratorContact"
            label="Customer Address Address"
            value={value}
          />
        </>
      );
    }

    if (
      [fieldExistingCustomerBA, fieldExistingCustomerDomain].includes(fieldKey)
    ) {
      label = getUpdateLabel(fieldKey, label);
    }

    if (
      fieldKey === fieldExistingBusinessConnectBANNumber &&
      backendObject[fieldBAN]
    ) {
      return null;
    }
    const channelName = _.get(channels, '0.description', '');
    if (
      _.get(role, 'id', 0) === 2 &&
      Object.keys(smbIndirectSalesChannels).includes(channelName) &&
      fieldKey === keyOutlet
    ) {
      const selectData = {
        label,
        ref,
        type: 'Select',
        name: fieldKey,
        required: true,
        value: backendObject[fieldKey],
        options: Object.values(smbIndirectSalesChannels).map(channel => {
          return { fields: { label: channel[0], value: channel[0] } };
        }),
        onChange: e => handleFormField(e.target, true)
      };
      return <FormField data={selectData} error={error} />;
    }

    /** For role sales support show a channel dropdown to select from
     * before they start creating orders
     */
    if (isSalesSupport && fieldKey === keyChannelName && isSelectAllowed) {
      if (Array.isArray(channels) && channels.length > 1) {
        const options = channels.map(item => ({
          fields: { value: item.id, label: item.description }
        }));
        const selectData = {
          label,
          ref,
          type: 'Select',
          name: keyChannelId,
          required: true,
          value: '',
          options: [...options],
          onChange: e => handleFormField(e.target, true)
        };

        return <FormField data={selectData} error={error} />;
      }
    }

    /**
     * If coper to fibre migration is No
     * then hide the row from the order summary
     */
    if (
      fieldKey === fieldCoperToFibreMigration &&
      getFieldValue(fieldKey, value) === no
    )
      return null;

    /**
     * For Salessupport role if on prequalification screen
     * only show the channel field and hide rest of them
     * while on order summary screen show all fields
     */
    if (isSalesSupport && fieldKey !== keyChannelName && isSelectAllowed) {
      return null;
    }

    /** For Fleet Complete updating the field label */
    if (
      key === fieldCustomerMigrating &&
      backendObject[fieldIotProductSelected] === valueFleetcomplete
    ) {
      label = labelFieldCustomerMigrationFC;
    }

    /** For Fleet Complete adding shipping charges along side shipping method */
    if (
      key === fieldIotDeliveryOptions &&
      backendObject[fieldIotProductSelected] === valueFleetcomplete
    ) {
      const shippingCharges = _.get(backendObject, 'total.shippingCharges', 0);
      const fieldValueShipping = `${getFieldValue(
        fieldKey,
        value
      )} - ${getFormattedPricing(shippingCharges)}`;
      return (
        <FormSummaryRow key={key} label={label} value={fieldValueShipping} />
      );
    }
    if (
      (key === fieldAdditionalLocalNumber ||
        key === fieldAdditionalTollFreeNumber) &&
      value !== undefined
    ) {
      return (
        <FormSummaryRow key={key} label={label} value={value ? 'Yes' : 'No'} />
      );
    }
    return (
      <FormSummaryRow
        key={key}
        label={label}
        value={
          typeof value === 'boolean'
            ? fieldValue
            : getFieldValue(fieldKey, value)
        }
      />
    );
  });

  let isSectionNull = true;
  fieldData.forEach(item => {
    if (item != null && _.get(item, 'props.value', true)) isSectionNull = false;
  });

  // Check if we're in Offer Configuration section
  const appliedCoupons = getDisplayCoupons(backendObject);
  const hasAppliedCoupons =
    appliedCoupons &&
    Array.isArray(appliedCoupons) &&
    appliedCoupons.length > 0;

  // Check multiple ways to identify Offer Configuration section
  const isOfferConfig =
    backendKey === fieldOfferConfiguration ||
    name === fieldOfferConfiguration ||
    (data &&
      data.fields &&
      (data.fields.name === fieldOfferConfiguration ||
        data.fields.backendKey === fieldOfferConfiguration)) ||
    (name &&
      typeof name === 'string' &&
      name.toLowerCase().includes('offer') &&
      name.toLowerCase().includes('configuration')) ||
    (backendKey &&
      typeof backendKey === 'string' &&
      backendKey.toLowerCase().includes('offer') &&
      backendKey.toLowerCase().includes('configuration'));

  if (isSectionNull && backendObject.recontractCustomer === 'Yes') {
    const localizedStrings = useLocalizedStringsContext();
    const {
      general: { notApplicable }
    } = localizedStrings;
    return <NoInformationFound message={notApplicable} />;
  }
  if (isSectionNull) return <NoInformationFound />;

  return (
    <>
      {isOfferConfig && hasAppliedCoupons && (
        <AppliedCouponsSummaryTable appliedCoupons={appliedCoupons} />
      )}
      {fieldData}
    </>
  );
};

FormSummaryGroup.propTypes = {
  data: PropTypes.shape({
    fields: PropTypes.shape({
      backendFields: PropTypes.arrayOf(PropTypes.shape({})),
      backendKey: PropTypes.string,
      name: PropTypes.string,
      backendFieldValidation: PropTypes.shape({})
    })
  }).isRequired,
  orderData: PropTypes.shape({
    role: PropTypes.string,
    channels: PropTypes.arrayOf(PropTypes.shape({})),
    devices: PropTypes.arrayOf(PropTypes.shape({})),
    handleFormField: PropTypes.func,
    error: PropTypes.shape({}),
    ref: PropTypes.shape({}),
    isSelectAllowed: PropTypes.bool,
    businessAppPlans: PropTypes.arrayOf(PropTypes.shape({})),
    productInOrderFor: PropTypes.string,
    recontractCustomer: PropTypes.string
  }).isRequired
};

export default FormSummaryGroup;
